/**
 * What each place the viewer appears actually needs from it.
 *
 * This replaces `instanceKey: string`, which conflated four unrelated decisions - which
 * feedback options to offer, how to label a detection box, which badges the filmstrip
 * shows, and whether thumbnails load in a window - and answered them with string
 * comparisons scattered across four files. Three of the six keys had constants and
 * predicates; the other three (`licedetections`, `laksvelDetections`, `catchoftheday`) were
 * compared inline, and five of the seven call sites passed a raw literal rather than the
 * constant that existed.
 *
 * Everything derived from the variant is in one table, so adding a variant is a matter of
 * answering these four questions rather than finding every `=== '...'` in the tree.
 */
export type ViewerVariant =
    | 'bioscope'
    | 'catchOfTheDay'
    | 'liceDetections'
    | 'woundDetections'
    | 'healthWoundInsight'
    | 'maturationDetections'
    | 'laksvelDetections'
    | 'healthDetections';

/** Which set of "wrong detection" reasons the feedback dropdown offers. */
export type FeedbackOptionSet = 'lice' | 'health' | 'laksvel' | 'generic';

/** How a box's hover label is composed. */
export type BoxTooltipStyle =
    /** `tag - 12.34cm²`, from the measured size. */
    | 'woundArea'
    /** `tag - 85%`, from the confidence score. OptoScale admins only. */
    | 'liceScore'
    | 'tagOnly';

/** Which counts the filmstrip thumbnails are badged with. */
export type FilmstripLabels = 'wound' | 'lice' | 'laksvel' | 'none';

export interface VariantBehaviour {
    feedbackOptions: FeedbackOptionSet;
    boxTooltip: BoxTooltipStyle;
    filmstripLabels: FilmstripLabels;
    /**
     * Load thumbnails in a window around the selection rather than lazily on scroll.
     *
     * Bioscope cages have thousands of frames per day, and an IntersectionObserver-driven
     * strip of that many entries fetches far too much.
     */
    windowFilmstripJpegs: boolean;
}

export const VIEWER_VARIANTS: Record<ViewerVariant, VariantBehaviour> = {
    bioscope: {
        feedbackOptions: 'generic',
        boxTooltip: 'tagOnly',
        filmstripLabels: 'none',
        windowFilmstripJpegs: true,
    },
    catchOfTheDay: {
        feedbackOptions: 'generic',
        boxTooltip: 'tagOnly',
        filmstripLabels: 'none',
        windowFilmstripJpegs: false,
    },
    liceDetections: {
        feedbackOptions: 'lice',
        boxTooltip: 'liceScore',
        filmstripLabels: 'lice',
        windowFilmstripJpegs: false,
    },
    woundDetections: {
        feedbackOptions: 'health',
        boxTooltip: 'woundArea',
        filmstripLabels: 'wound',
        windowFilmstripJpegs: false,
    },
    healthWoundInsight: {
        feedbackOptions: 'health',
        boxTooltip: 'woundArea',
        filmstripLabels: 'wound',
        windowFilmstripJpegs: false,
    },
    // Same detection type as woundDetections - both were `instanceKey="detections"` - but
    // the filmstrip shows no wound counts here.
    maturationDetections: {
        feedbackOptions: 'health',
        boxTooltip: 'woundArea',
        filmstripLabels: 'none',
        windowFilmstripJpegs: false,
    },
    laksvelDetections: {
        feedbackOptions: 'laksvel',
        boxTooltip: 'tagOnly',
        filmstripLabels: 'laksvel',
        windowFilmstripJpegs: false,
    },
    // The health parameters that have no laksvel score to badge. Previously the same
    // instanceKey as laksvelDetections with a second viewerConfig object to suppress the
    // badges.
    healthDetections: {
        feedbackOptions: 'laksvel',
        boxTooltip: 'tagOnly',
        filmstripLabels: 'none',
        windowFilmstripJpegs: false,
    },
};

export function variantBehaviour(variant: ViewerVariant): VariantBehaviour {
    return VIEWER_VARIANTS[variant];
}
